/**
 * WorkflowColumnsSection — gerencia colunas (custom + defaults) de um workflow.
 *
 * Renderizado dentro do WorkflowFormDialog quando editing existe (BUG-V2-020).
 * Defaults code-side aparecem como read-only (com label sobreposto).
 * Custom columns ganham botão lixeira.
 *
 * Backend retorna 409 com cards bloqueantes — UI mostra mensagem amigável
 * "X cards bloqueando: ..." em vez de erro genérico.
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';

import { Alert, Button, useConfirmDialog } from '@/components/ui';
import { useKanbanColumns, type DefaultStage } from '@/lib/use-kanban-columns';
import { DEAL_STAGES } from '@/lib/comercial-types';

interface BlockingCard {
  id: string;
  label: string;
  type: 'deal' | 'service_order';
}

interface WorkflowColumnsSectionProps {
  workflowSlug: string;
  deptSlug: string;
}

/**
 * BUG-V2-020/037 (2026-05-24): pós-Migration 045, todas as colunas vivem no
 * banco (`kanban_custom_columns`). Esta função mantém o nome legado mas só
 * existe agora como FALLBACK defensivo — backend offline ou deploy pré-045.
 * Quando o banco tem as 6 seeds DEAL_STAGES, useKanbanColumns dedupliea
 * preferindo o registro do banco.
 */
function defaultsForDept(deptSlug: string): DefaultStage[] {
  if (deptSlug === 'comercial') {
    return DEAL_STAGES.map((s, i) => ({
      slug: s.slug,
      label: s.label,
      ordem: i + 1,
      variant: s.variant,
    }));
  }
  return [];
}

export function WorkflowColumnsSection({ workflowSlug, deptSlug }: WorkflowColumnsSectionProps) {
  const defaults = defaultsForDept(deptSlug);
  // BUG-V2-020/037: removida desestruturação de `isDefault` — todas as colunas
  // são deletáveis pelo admin pós-Migration 045 (banco vira source of truth).
  const { columns, removeColumn } = useKanbanColumns(workflowSlug, defaults);

  const [error, setError] = useState<string | null>(null);
  const [blocking, setBlocking] = useState<BlockingCard[] | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  // FASE 2 / M1: ConfirmDialog do design system (substitui confirm() nativo)
  const confirm = useConfirmDialog();

  async function handleDelete(slug: string, label: string) {
    // BUG-V2-037 (2026-05-24): se for a ÚLTIMA coluna visível do workflow,
    // confirma com mensagem forte explicando consequência (workflow ficará
    // sem colunas e não aceita novos cards até admin criar uma nova).
    // ConfirmDialog ainda usa variant 'destructive' por consistência visual
    // — não temos variant 'warning' no design system; a mensagem cumpre o
    // papel de aviso.
    const isLastColumn = columns.length === 1;
    const ok = await confirm({
      title: isLastColumn ? 'Excluir a última coluna?' : 'Excluir coluna',
      message: isLastColumn
        ? `Excluir a coluna "${label}" deixa este fluxo SEM colunas. O quadro fica vazio e não aceita novos cards até você criar outra coluna. Tem certeza?`
        : `Excluir a coluna "${label}"? Esta ação não pode ser desfeita.`,
      confirmLabel: isLastColumn ? 'Sim, deixar sem colunas' : 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    setError(null);
    setBlocking(null);
    setPending(slug);
    try {
      await removeColumn(slug);
    } catch (err) {
      // Backend retorna 409 com blockingCards quando há cards na coluna
      // (BUG-V2-020). Regra "última coluna" removida no backend pelo
      // BUG-V2-037 — frontend usa mensagem amigável acima como guard.
      const e = err as { response?: { error?: string; blockingCards?: BlockingCard[]; message?: string } };
      if (e?.response?.error === 'column-has-cards') {
        setError(
          e.response.message ??
            'Ainda há cards nesta coluna. Mova ou encerre os cards abaixo antes de excluir.',
        );
        setBlocking(e.response.blockingCards ?? []);
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao excluir coluna');
      }
    } finally {
      setPending(null);
    }
  }

  return (
    <div data-testid="workflow-columns-section" className="mt-4 p-3 border border-line dark:border-line-dark rounded-md">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
          Colunas do quadro
        </h4>
        <span className="text-xs text-ink-3 dark:text-ink-dark-3 tabular-nums">
          {columns.length} {columns.length === 1 ? 'coluna' : 'colunas'}
        </span>
      </div>

      {error && (
        <Alert variant="danger" rail className="mb-2 text-xs">
          {error}
          {blocking && blocking.length > 0 && (
            <ul className="mt-1 ml-3 list-disc text-xs space-y-0.5">
              {blocking.map((b) => (
                <li key={b.id} className="break-words">
                  {b.label}{' '}
                  <span className="text-ink-3 dark:text-ink-dark-3">
                    ({b.type === 'deal' ? 'negócio' : 'ordem de serviço'})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      <ul className="space-y-1">
        {columns.map((c) => (
          <li
            key={c.slug}
            data-testid={`workflow-column-${c.slug}`}
            className="flex items-center justify-between gap-3 px-2 py-1.5 rounded hover:bg-paper-warm dark:hover:bg-paper-dark-warm text-sm"
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-ink-1 dark:text-ink-dark-1">{c.label}</span>
              <span className="font-mono text-xs text-ink-3 dark:text-ink-dark-3 break-all">
                {c.slug}
              </span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="shrink-0"
              onClick={() => handleDelete(c.slug, c.label)}
              disabled={pending === c.slug}
              data-testid={`workflow-column-delete-${c.slug}`}
              aria-label={`Excluir coluna ${c.label}`}
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>

      <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-2 italic">
        Qualquer coluna pode ser excluída — antes, mova ou encerre os cards que estão nela.
      </p>
    </div>
  );
}
